import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { SubscriptionPlan } from '../../models/platform-access.models';
import { AuthService, User } from '../../services/auth.service';
import {
  BillingApiError,
  BillingCheckoutMode,
  BillingCheckoutState,
  BillingSummary,
  BillingService,
} from '../../services/billing.service';
import { PaddleCheckoutEvent, PaddleJsService } from '../../services/paddle-js.service';

@Component({
  selector: 'app-billing-checkout-page',
  standalone: true,
  imports: [RouterLink, FormsModule, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './billing-checkout-page.component.html',
  styleUrls: ['./billing-checkout-page.component.scss']
})
export class BillingCheckoutPageComponent implements OnInit {
  private static readonly MAX_COMPLETE_RETRIES = 8;
  private static readonly COMPLETE_RETRY_DELAY_MS = 1500;
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  readonly checkoutFrameTarget = 'billing-paddle-frame';

  isInitialLoading = true;
  hasInitialLoadError = false;
  isActionBusy = false;
  isPortalBusy = false;
  isCheckoutMounting = false;
  isCheckoutMounted = false;
  isCheckoutCompleting = false;
  checkoutError = '';
  pageMessage = typeof history !== 'undefined' ? history.state?.billingMessage ?? '' : '';
  selectedPlanCode = '';
  currentUser: User | null = null;
  billing: BillingSummary | null = null;
  selectedPlan: SubscriptionPlan | null = null;
  checkoutMode: BillingCheckoutMode | null = null;
  summaryMessage = '';
  consentText = '';
  consentAccepted = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly billingService: BillingService,
    private readonly paddleJsService: PaddleJsService,
    private readonly authService: AuthService
  ) {
    this.destroyRef.onDestroy(() => {
      this.paddleJsService.closeCheckout();
    });
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        map((params) => params.get('plan') || ''),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((planCode) => {
        this.runInAngular(() => {
          this.selectedPlanCode = planCode;
          this.consentAccepted = false;
          this.isCheckoutMounted = false;
          this.checkoutError = '';
          this.pageMessage = '';
        });
        this.loadCheckoutState();
      });
  }

  get isInlineCheckoutMode(): boolean {
    return this.checkoutMode === 'inline_checkout';
  }

  get isDirectChangeMode(): boolean {
    return this.checkoutMode === 'upgrade_immediate'
      || this.checkoutMode === 'downgrade_next_cycle'
      || this.checkoutMode === 'cancel_next_cycle';
  }

  get canOpenPortal(): boolean {
    return !!this.billing?.portalAvailable;
  }

  get directActionLabel(): string {
    switch (this.checkoutMode) {
      case 'upgrade_immediate':
        return 'Confirm upgrade';
      case 'downgrade_next_cycle':
        return 'Schedule downgrade';
      case 'cancel_next_cycle':
        return 'Schedule move to Free';
      default:
        return 'Continue';
    }
  }

  get directActionClass(): string {
    return this.checkoutMode === 'cancel_next_cycle' ? 'btn btn-outline-danger' : 'btn btn-primary';
  }

  formatPlanPrice(plan: SubscriptionPlan | User['plan']): string {
    if (plan.priceEur === 0) {
      return 'Free';
    }

    return `${plan.priceEur} EUR / ${plan.billingInterval}`;
  }

  loadCheckoutState(): void {
    this.paddleJsService.closeCheckout();
    this.isCheckoutMounted = false;
    this.checkoutError = '';
    this.isInitialLoading = true;
    this.hasInitialLoadError = false;

    if (!this.selectedPlanCode) {
      this.runInAngular(() => {
        this.selectedPlan = null;
        this.currentUser = null;
        this.billing = null;
        this.checkoutMode = null;
        this.summaryMessage = '';
        this.consentText = '';
        this.isInitialLoading = false;
      });
      return;
    }

    this.billingService.prepareCheckout(this.selectedPlanCode, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.applyCheckoutState(state, true);
        },
        error: () => {
          this.runInAngular(() => {
            this.hasInitialLoadError = true;
            this.isInitialLoading = false;
          });
        }
      });
  }

  launchInlineCheckout(): void {
    if (!this.selectedPlan || !this.currentUser || !this.isInlineCheckoutMode) {
      return;
    }

    if (!this.consentAccepted) {
      this.checkoutError = 'You must acknowledge immediate access before loading the secure checkout.';
      return;
    }

    this.checkoutError = '';
    this.isCheckoutMounting = true;
    this.isCheckoutMounted = false;

    this.billingService.prepareCheckout(this.selectedPlan.code, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (state) => {
          this.applyCheckoutState(state, false);

          if (!state.checkout) {
            this.runInAngular(() => {
              this.checkoutError = 'Checkout configuration is not available for this plan yet.';
              this.isCheckoutMounting = false;
            });
            return;
          }

          try {
            await this.paddleJsService.openInlineCheckout({
              clientToken: state.checkout.clientToken,
              environment: state.checkout.environment,
              frameTarget: this.checkoutFrameTarget,
              customerId: state.checkout.customerId,
              customerAuthToken: state.checkout.customerAuthToken,
              priceId: state.checkout.priceId,
              customData: state.checkout.customData,
              onEvent: (event) => this.handlePaddleEvent(event)
            });

            this.runInAngular(() => {
              this.isCheckoutMounted = true;
              this.isCheckoutMounting = false;
            });
          } catch (error) {
            this.runInAngular(() => {
              this.checkoutError = error instanceof Error ? error.message : 'Could not open the Paddle checkout.';
              this.isCheckoutMounting = false;
            });
          }
        },
        error: (error: BillingApiError) => {
          this.runInAngular(() => {
            this.checkoutError = error.message;
            this.isCheckoutMounting = false;
          });
        }
      });
  }

  confirmDirectChange(): void {
    if (!this.selectedPlan || !this.isDirectChangeMode) {
      return;
    }

    this.checkoutError = '';
    this.isActionBusy = true;

    this.billingService.changePlan(this.selectedPlan.code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.runInAngular(() => {
            this.authService.setCurrentUser(response.user);
            this.router.navigate(['/billing/plans'], {
              state: { billingMessage: response.message }
            });
          });
        },
        error: (error: BillingApiError) => {
          this.runInAngular(() => {
            this.checkoutError = error.message;
            this.isActionBusy = false;
          });
        }
      });
  }

  openBillingPortal(): void {
    if (!this.canOpenPortal) {
      return;
    }

    this.checkoutError = '';
    this.isPortalBusy = true;

    this.billingService.createPortalSession()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (url) => {
          this.runInAngular(() => {
            this.isPortalBusy = false;
          });
          const portalWindow = window.open(url, '_blank', 'noopener,noreferrer');
          if (!portalWindow) {
            this.runInAngular(() => {
              this.checkoutError = 'The billing portal was blocked by the browser. Allow popups and try again.';
            });
          }
        },
        error: (error: BillingApiError) => {
          this.runInAngular(() => {
            this.checkoutError = error.message;
            this.isPortalBusy = false;
          });
        }
      });
  }

  private handlePaddleEvent(event: PaddleCheckoutEvent): void {
    if (event.name === 'checkout.completed') {
      void this.completeCheckout(event);
      return;
    }

    if (event.name === 'checkout.error' || event.name === 'checkout.payment.error') {
      this.runInAngular(() => {
        this.checkoutError = this.resolveCheckoutErrorMessage(event);
        this.isCheckoutMounting = false;
      });
      return;
    }

    if (event.name === 'checkout.closed') {
      this.runInAngular(() => {
        this.isCheckoutMounting = false;
      });
    }
  }

  private resolveCheckoutErrorMessage(event: PaddleCheckoutEvent): string {
    if (event.code === 'transaction_default_checkout_url_not_set') {
      return 'Paddle checkout is not fully configured yet. Set a default payment link in the Paddle sandbox checkout settings, then try again.';
    }

    if (event.detail) {
      return event.detail;
    }

    return 'Paddle reported a checkout error. Review the payment details and try again.';
  }

  private async completeCheckout(event: PaddleCheckoutEvent): Promise<void> {
    await this.completeCheckoutAttempt(event, 0);
  }

  private async completeCheckoutAttempt(event: PaddleCheckoutEvent, attempt: number): Promise<void> {
    if (!this.selectedPlan || this.isCheckoutCompleting) {
      return;
    }

    const customerId = event.data?.customer?.id;
    const priceId = event.data?.items?.[0]?.price_id ?? event.data?.items?.[0]?.price?.id ?? null;
    const transactionId = event.data?.transaction_id ?? null;

    if (!customerId || !transactionId) {
      this.runInAngular(() => {
        this.checkoutError = 'Checkout completed but the Paddle transaction details were incomplete. Refresh the billing page in a few seconds if the plan does not update automatically.';
      });
      return;
    }

    this.runInAngular(() => {
      this.checkoutError = '';
      this.isCheckoutCompleting = true;
      this.isActionBusy = true;
    });

    this.billingService.completeCheckout({
      planCode: this.selectedPlan.code,
      customerId,
      transactionId,
      priceId,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.runInAngular(() => {
            this.authService.setCurrentUser(response.user);
            this.router.navigate(['/billing/plans'], {
              state: { billingMessage: response.message }
            });
          });
        },
        error: (error: BillingApiError) => {
          if (
            error.message === 'The Paddle checkout transaction is not completed yet'
            && attempt < BillingCheckoutPageComponent.MAX_COMPLETE_RETRIES
          ) {
            this.runInAngular(() => {
              this.checkoutError = '';
              this.pageMessage = 'Payment succeeded. Waiting for Paddle to finalize the transaction...';
              this.isCheckoutCompleting = false;
              this.isActionBusy = false;
            });

            window.setTimeout(() => {
              void this.completeCheckoutAttempt(event, attempt + 1);
            }, BillingCheckoutPageComponent.COMPLETE_RETRY_DELAY_MS);
            return;
          }

          this.runInAngular(() => {
            this.pageMessage = '';
            this.checkoutError = error.message;
            this.isCheckoutCompleting = false;
            this.isActionBusy = false;
          });
        }
      });
  }

  private applyCheckoutState(state: BillingCheckoutState, resetLoading: boolean): void {
    this.runInAngular(() => {
      this.currentUser = state.currentUser;
      this.billing = state.billing;
      this.selectedPlan = state.plan;
      this.checkoutMode = state.mode;
      this.summaryMessage = state.summaryMessage;
      this.consentText = state.consentText;
      if (resetLoading) {
        this.isInitialLoading = false;
      }
    });
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
